<template>
  <div class="country-phone-input">
    <input ref="input" :value="modelValue" name="whatsapp" type="tel" autocomplete="tel" :aria-label="label" :placeholder="label" @input="syncValue">
  </div>
</template>

<script>
export default {
  name: 'CountryPhoneInput',
  props: {
    modelValue: { type: String, default: '' },
    country: { type: String, default: 'us' },
    label: { type: String, required: true },
    countryLabel: { type: String, required: true }
  },
  emits: ['update:modelValue', 'update:country'],
  mounted() {
    this._mobileClassWasPresent = document.body.classList.contains('iti-mobile')
    const input = this.phoneInput()
    const ready = input.intlTelInput({
      initialCountry: this.country, separateDialCode: true,
      syncCountryFields: false, utilsScript: '/static/js/intlTelUtils.js',
      dropdownContainer: this.$el
    })
    input.on('countrychange.contactPhone', this.syncValue)
    const flag = this.$el.querySelector('.selected-flag')
    flag.setAttribute('role', 'button')
    flag.setAttribute('aria-label', this.countryLabel)
    flag.setAttribute('aria-haspopup', 'listbox')
    this._observer = new MutationObserver(() => flag.setAttribute('aria-expanded', String(this.isCountryListOpen())))
    this._observer.observe(input.data('plugin_intlTelInput').countryList[0], { attributes: true, attributeFilter: ['class'] })
    flag.setAttribute('aria-expanded', 'false')
    document.addEventListener('pointerdown', this.closeOnOutside, true)
    this._ready = Promise.resolve(ready).then(() => { if (!this._disposed) this.syncValue() })
  },
  watch: {
    modelValue(value) {
      const input = this.phoneInput()
      if (input.val() !== value) input.intlTelInput('setNumber', value)
    },
    country(value) {
      const input = this.phoneInput()
      if (input.intlTelInput('getSelectedCountryData').iso2 !== value) input.intlTelInput('setCountry', value)
    },
    countryLabel(value) { this.$el.querySelector('.selected-flag').setAttribute('aria-label', value) }
  },
  beforeUnmount() {
    this._disposed = true
    this._observer?.disconnect()
    document.removeEventListener('pointerdown', this.closeOnOutside, true)
    this.closeCountryList()
    this.phoneInput().off('.contactPhone').intlTelInput('destroy')
    if (!this._mobileClassWasPresent && !document.querySelector('.intl-tel-input')) document.body.classList.remove('iti-mobile')
  },
  methods: {
    phoneInput() { return window.jQuery(this.$refs.input) },
    syncValue() {
      const input = this.phoneInput()
      this.$emit('update:modelValue', String(input.val()).trim())
      this.$emit('update:country', input.intlTelInput('getSelectedCountryData').iso2 || 'us')
    },
    isCountryListOpen() { return !!this.$el.querySelector('.country-list:not(.hide)') },
    closeCountryList() { this.phoneInput().data('plugin_intlTelInput')?._closeDropdown() },
    closeOnOutside(event) { if (!this.$el.contains(event.target)) this.closeCountryList() },
    focus() { this.$refs.input.focus() },
    async getNumber() {
      await this._ready
      if (this._disposed || !window.intlTelInputUtils) throw new Error('Phone formatting is unavailable')
      const input = this.phoneInput()
      // setNumber also strips an already-present international prefix before
      // getNumber adds the selected dial code in separateDialCode mode.
      input.intlTelInput('setNumber', input.val())
      const number = input.intlTelInput('getNumber')
      if (!number) throw new Error('Phone formatting is unavailable')
      return number
    }
  }
}
</script>

<style scoped>
.country-phone-input { position: relative; min-width: 0; }
.country-phone-input :deep(.intl-tel-input) { display: block; width: 100%; }
.country-phone-input input {
  display: block; width: 100%; height: 30px; margin: 0;
  padding: 0 8px 8px 110px !important; color: #000; background: transparent;
  border: 0; border-radius: 0; outline: none;
  font: 14px/1.4 'MiSans-Regular', 'Segoe UI', sans-serif;
}
.country-phone-input input::placeholder { color: rgba(0, 0, 0, 0.3); opacity: 1; }
.country-phone-input :deep(.flag-container) { padding: 0 0 8px 12px; }
.country-phone-input :deep(.selected-flag) { width: 90px !important; height: 100%; padding-left: 8px; }
.country-phone-input :deep(.selected-dial-code) { color: #000; font: 14px/1.4 'MiSans-Regular', 'Segoe UI', sans-serif; }
.country-phone-input :deep(.selected-flag:focus-visible) { outline: 2px solid #ef0a6a; outline-offset: 1px; }
.country-phone-input :deep(.iti-container) {
  position: absolute; top: 30px !important; left: 0 !important; right: 0; bottom: auto; z-index: 10;
}
.country-phone-input :deep(.country-list) {
  position: relative; width: 100%; max-height: 160px; margin: 0;
  white-space: normal; color: #000; font: 13px/1.4 'MiSans-Regular', 'Segoe UI', sans-serif;
}
.country-phone-input :deep(.country-list.dropup) { bottom: auto; }
@media (max-width: 700px) {
  .country-phone-input input, .country-phone-input :deep(.selected-dial-code) { font-size: 13px; }
}
</style>
